/**
 * Карточка сотрудника (company-structure + p2-ot-section §9.4): личные данные + блок «Условия
 * труда — по последней СОУТ» (реальная сводка или «нет данных») + блок «Информация по
 * профрискам» (сводка или «нет данных») + блок «Обучения» (§3e). Отдел и должность показываются
 * из рабочего места (на человеке не дублируются). В карточке НЕТ кнопок перехода к заказу
 * обучения/СОУТ/профрисков (§9.4/design D5) — только «Добавить историческое обучение» (точка
 * входа, design D8, §11.1; сама форма — `HistoricalTrainingForm`, p2-manual-historical/IC-4).
 * §1.1/§1.2 (p3-structure-sout-training, design D5/D6): в блоках «Условия труда»/«Информация по
 * профрискам» — кнопки «Добавить историческую СОУТ»/«Добавить исторические профриски», единые
 * панели `HistoricalSoutPanel`/`HistoricalProfriskPanel` с автоподстановкой единственного места
 * сотрудника (`employee.workplaceId`, модель one-place-per-person). Внесённые исторические записи
 * показаны в этих блоках сразу, с «Править»/«Удалить» (`inlineRecords`): за кнопкой добавления
 * их не находили.
 *
 * Компоновка (ТЗ 26.10.2026, п.5.2; ответ заказчицы 04.11): в верхней строке — «Назад» слева,
 * справа «Активен», ФИО, «Изменить информацию» и «Уволен(а)»; ниже слева — «Информация» (шире,
 * поля одной строкой «Пол: Женский»), справа — «Условия труда», «Профриски» и «Обучения». В
 * «Обучениях» — строка индексов (А, Б, ПП, СИЗ, В1…) цветом статуса, программы — как в каталоге
 * (плашка документа, краткое название · часы, ниже официальное) и по разделам каталога. На узком
 * экране — одна колонка.
 */
import { useMemo, useState, type ReactNode } from 'react'
import { AlertTriangle, ChevronDown, GraduationCap, ShieldCheck } from 'lucide-react'
import type {
  EducationDocumentStatus,
  EmployeeCard,
  Program,
  TrainingConsentStatus,
  TrainingDirectionItem,
  TrainingRecord,
  TrainingStatus,
} from '@/lib/api'
import { TRAINING_STATUS_VIEW, shortTerm } from '@/lib/training'
import { flattenDirections } from '@/lib/training-catalog'
import { cn } from '@/lib/utils'
import { ProgramTitle, type ProgramTitleData } from '@/training/program-title'
import { RISK_LEVEL_LABELS } from '@/lib/profrisk'
import {
  ConsentCell,
  EducationDocumentCell,
  type ConsentApi,
  type EducationDocumentApi,
} from '@/training/employee-documents'
import { Card } from '@/design-system/components/layout/card'
import { Badge } from '@/design-system/components/primitives/badge'
import { Button } from '@/design-system/components/primitives/button'
import { EmptyState } from '@/design-system/components/feedback/empty-state'
import { HistoricalTrainingForm } from '@/training/historical-training-form'
import { HistoricalSoutPanel } from '@/sout/historical-sout-form'
import { HistoricalProfriskPanel } from '@/profrisk/historical-profrisk-form'

/** Поле блока «Информация» одной строкой «Пол: Женский» (ответ заказчицы 04.11 к п.5.2). */
function InfoField({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-1.5 border-b border-line py-1.5 last:border-b-0">
      <span className="text-txt-secondary">
        {label}
        <span aria-hidden>:</span>
      </span>
      <span className="min-w-0 break-words font-medium text-txt">{value || '—'}</span>
    </div>
  )
}

/** Цвет индекса по статусу последнего обучения с ним: действует — зелёный, внимание — жёлтый. */
const INDEX_TONE: Record<TrainingStatus, string> = {
  valid: 'bg-green-bg text-green',
  expiring: 'bg-amber-bg text-amber',
  expired: 'bg-red-bg text-red',
  in_process: 'bg-gray-bg text-gray',
}

/**
 * Индексы обучения сотрудника (ответ заказчицы 04.11 к п.5.2): А, Б, ПП, СИЗ, В1… — по записям
 * охраны труда; статус индекса — по самой поздней записи с ним. Порядок — как в каталоге:
 * А, Б, В1…, ПП, СИЗ, прочие по алфавиту.
 */
export function employeeIndices(
  records: TrainingRecord[],
): { index: string; status: TrainingStatus }[] {
  const latest = new Map<string, TrainingRecord>()
  for (const r of records) {
    if (r.directionSystemCode !== 'ot') continue
    for (const idx of r.otIndices) {
      const prev = latest.get(idx)
      if (!prev || (r.lastTrainedOn ?? '') > (prev.lastTrainedOn ?? '')) latest.set(idx, r)
    }
  }
  const order = (idx: string) => (['А', 'Б'].includes(idx) ? 0 : idx.startsWith('В') ? 1 : 2)
  return [...latest.entries()]
    .sort(([a], [b]) => order(a) - order(b) || a.localeCompare(b, 'ru', { numeric: true }))
    .map(([index, r]) => ({ index, status: r.status }))
}

/** Строка программы записи — как в каталоге (без «Инфо»: в карточке нужны название и документ). */
function recordProgram(r: TrainingRecord): ProgramTitleData {
  return {
    name: r.programName,
    shortName: r.programShortName ?? '',
    hours: r.programHours ?? null,
    documentType: r.programDocumentType ?? '',
    audience: [],
    recurrenceMode: r.recurrenceMode,
    recurrenceMonths: null,
    periodText: '',
    educationBase: 'any',
    rank: '',
    profStandard: '',
    note: '',
  }
}

/** Худший статус группы: просрочено → истекает → в процессе → в силе. */
const STATUS_WEIGHT: Record<TrainingStatus, number> = {
  expired: 3,
  expiring: 2,
  in_process: 1,
  valid: 0,
}

interface RecordGroup {
  key: string
  name: string
  /** Записи группы с подписью подраздела (путь ниже корня) — в порядке дерева. */
  items: { record: TrainingRecord; subsection: string }[]
}

/**
 * Записи по корневым разделам каталога (ответ заказчицы 04.11 к п.5.2: «Охрана труда, раскрываем,
 * под ним перечень программ»): порядок разделов и подразделов — как в дереве; запись без раздела
 * в дереве — в «Прочее».
 */
export function groupRecordsByCatalog(
  records: TrainingRecord[],
  directions: TrainingDirectionItem[],
): RecordGroup[] {
  const nodes = flattenDirections(directions)
  const position = new Map(nodes.map((n, i) => [n.direction.id, i]))
  const byId = new Map(nodes.map((n) => [n.direction.id, n]))
  const groups = new Map<string, RecordGroup>()
  const sorted = [...records].sort(
    (a, b) =>
      (position.get(a.directionId ?? '') ?? Number.MAX_SAFE_INTEGER) -
        (position.get(b.directionId ?? '') ?? Number.MAX_SAFE_INTEGER) ||
      (a.programShortName || a.programName).localeCompare(
        b.programShortName || b.programName,
        'ru',
      ),
  )
  for (const r of sorted) {
    const node = r.directionId ? byId.get(r.directionId) : undefined
    const rootName = node ? node.path[0] : 'Прочее'
    const key = node
      ? (nodes.find((n) => n.depth === 1 && n.path[0] === rootName)?.direction.id ?? rootName)
      : '__other__'
    const group = groups.get(key) ?? { key, name: rootName, items: [] }
    group.items.push({ record: r, subsection: node ? node.path.slice(1).join(' / ') : '' })
    groups.set(key, group)
  }
  return [...groups.values()]
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center gap-3 border-b border-line py-2.5 last:border-b-0">
      <div className="flex-1 text-txt-secondary">{label}</div>
      <span className="text-right font-medium text-txt">{value || '—'}</span>
    </div>
  )
}

/**
 * Документ карточки только для просмотра/скачивания (карточка у команды): документ об образовании
 * (centrika-docs!59) и согласие на обработку ПД.
 */
function DocumentReadonly({
  loaded,
  fileName,
  url,
  emptyLabel,
  loadedLabel,
}: {
  loaded: boolean
  fileName?: string
  url?: string
  emptyLabel: string
  loadedLabel: string
}) {
  if (!loaded) return <span className="text-txt-secondary">{emptyLabel}</span>
  return url ? (
    <a className="text-accent underline" href={url} download>
      {fileName || 'Скачать файл'}
    </a>
  ) : (
    <span>{loadedLabel}</span>
  )
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/**
 * Блок «Обучения» (service-training §3e): все записи обучения слушателя из заказов Центрики и внешних
 * источников. По записи: программа (с разрядом), дата выдачи, срок/продление, статус-бейдж и документ;
 * охрана труда — с чипами-индексами (А/Б/СИЗ/ПП/В).
 *
 * У исторической записи (внесена вручную, source='external') — действия «Править» и «Удалить»:
 * ошибку ввода иначе не исправить, а новая запись с верной датой неверную не перекрывает (по
 * программе берётся самая поздняя дата обучения). Запись из заказа Центрики не меняется.
 */
function TrainingRecordRow({
  record,
  onEdit,
  onDelete,
  actionsDisabled,
}: {
  record: TrainingRecord
  onEdit?: () => void
  onDelete?: () => void
  actionsDisabled?: boolean
}) {
  const view = TRAINING_STATUS_VIEW[record.status]
  const editable = record.source === 'external' && (onEdit || onDelete)
  return (
    <div className="flex flex-col gap-1.5 border-b border-line py-3 last:border-b-0">
      <div className="flex flex-wrap items-start justify-between gap-2">
        {/* Название — как в каталоге (ответ заказчицы 04.11 к п.5.2). */}
        <div className="min-w-0 flex-1">
          <ProgramTitle program={recordProgram(record)} info={false} />
        </div>
        <Badge status={view.role}>{view.label}</Badge>
      </div>
      {record.directionSystemCode === 'ot' && record.otIndices.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {record.otIndices.map((idx) => (
            <span key={idx} className="rounded-pill bg-gray-bg px-1.5 py-0.5 text-11 text-gray">
              {idx}
            </span>
          ))}
        </div>
      ) : null}
      <div className="text-12 text-txt-tertiary">
        выдано {record.lastTrainedOn ? formatDate(record.lastTrainedOn) : '—'}
        {record.dueOn ? ` · продлить ${formatDate(record.dueOn)}` : ''} ·{' '}
        {shortTerm({
          status: record.status,
          dueOn: record.dueOn,
          recurrenceMode: record.recurrenceMode,
        })}
        {record.source === 'external' ? ' · внешнее' : ''}
      </div>
      {record.documentUrl ? (
        <a className="text-12 text-accent underline" href={record.documentUrl} download>
          Скачать документ
        </a>
      ) : null}
      {editable ? (
        <div className="flex flex-wrap gap-2">
          {onEdit ? (
            <Button
              size="sm"
              variant="secondary"
              disabled={actionsDisabled}
              aria-label={`Править историческое обучение: ${record.programName}`}
              onClick={onEdit}
            >
              Править
            </Button>
          ) : null}
          {onDelete ? (
            <Button
              size="sm"
              variant="secondary"
              disabled={actionsDisabled}
              aria-label={`Удалить историческое обучение: ${record.programName}`}
              onClick={onDelete}
            >
              Удалить
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function TrainingBlock({
  trainings,
  directions,
  personId,
  editingId,
  programs,
  actionsDisabled,
  onEdit,
  onDelete,
  onSaved,
  onCancelEdit,
}: {
  trainings: TrainingRecord[]
  /** Дерево разделов каталога — группировка записей; без него — одним списком. */
  directions?: TrainingDirectionItem[]
  personId: string
  /** Запись, открытая на правку: вместо её строки показывается форма. */
  editingId?: string
  programs?: Program[]
  actionsDisabled?: boolean
  onEdit?: (record: TrainingRecord) => void
  onDelete?: (record: TrainingRecord) => void
  onSaved?: () => void
  onCancelEdit?: () => void
}) {
  if (trainings.length === 0) {
    return (
      <EmptyState
        icon={GraduationCap}
        title="Записей об обучении пока нет"
        description="Записи появятся из заказов Центрики или при ручном вводе внешнего обучения"
      />
    )
  }
  const groups = groupRecordsByCatalog(trainings, directions ?? [])
  const renderRecord = (r: TrainingRecord) =>
    r.id === editingId && programs ? (
      <div key={r.id} className="border-b border-line pb-3 last:border-b-0">
        <HistoricalTrainingForm
          personId={personId}
          programs={programs}
          record={r}
          onSaved={onSaved ?? (() => {})}
          onCancel={onCancelEdit ?? (() => {})}
        />
      </div>
    ) : (
      <TrainingRecordRow
        key={r.id}
        record={r}
        actionsDisabled={actionsDisabled}
        onEdit={onEdit ? () => onEdit(r) : undefined}
        onDelete={onDelete ? () => onDelete(r) : undefined}
      />
    )
  return (
    <div className="flex flex-col gap-2">
      {groups.map((g) => (
        <RecordGroupSection
          key={g.key}
          group={g}
          // Единственный раздел раскрыт сразу — сворачивать нечего.
          defaultOpen={groups.length === 1}
          editingId={editingId}
          renderRecord={renderRecord}
        />
      ))}
    </div>
  )
}

/**
 * Раздел каталога в «Обучениях»: свёрнут (единственный — раскрыт), в заголовке — число программ и
 * худший статус; раскрытый показывает программы с подписью подраздела. Раздел с записью на правке
 * раскрыт.
 */
function RecordGroupSection({
  group,
  defaultOpen,
  editingId,
  renderRecord,
}: {
  group: RecordGroup
  defaultOpen: boolean
  editingId?: string
  renderRecord: (r: TrainingRecord) => ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  const isOpen = open || group.items.some((i) => i.record.id === editingId)
  const worst = group.items.reduce<TrainingStatus>(
    (w, i) => (STATUS_WEIGHT[i.record.status] > STATUS_WEIGHT[w] ? i.record.status : w),
    'valid',
  )
  let lastSubsection = ''
  return (
    <div className="rounded-10 border border-line">
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={() => setOpen((v) => !v)}
        className="ds-focus-ring flex w-full items-center gap-2 rounded-10 px-3 py-2 text-left hover:bg-hover"
      >
        <ChevronDown
          aria-hidden
          className={cn(
            'size-4 shrink-0 stroke-current text-txt-secondary transition-transform',
            !isOpen && '-rotate-90',
          )}
        />
        <span className="min-w-0 flex-1 font-semibold text-txt">{group.name}</span>
        <span className="text-12 text-txt-tertiary">{group.items.length}</span>
        <Badge status={TRAINING_STATUS_VIEW[worst].role}>{TRAINING_STATUS_VIEW[worst].label}</Badge>
      </button>
      {isOpen ? (
        <div className="border-t border-line px-3">
          {group.items.map(({ record, subsection }) => {
            const caption = subsection !== lastSubsection && subsection ? subsection : ''
            lastSubsection = subsection
            return (
              <div key={record.id}>
                {caption ? (
                  <p className="pt-2 text-12 font-semibold uppercase text-txt-tertiary">
                    {caption}
                  </p>
                ) : null}
                {renderRecord(record)}
              </div>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

export function EmployeeCardView({
  employee,
  backButton,
  directions,
  onToggleActive,
  toggling,
  onOpenWorkplace,
  onEdit,
  onAddHistoricalTraining,
  addingHistoricalTraining,
  addingHistoricalTrainingLoading,
  historicalTrainingPrograms,
  onHistoricalTrainingSaved,
  onCancelHistoricalTraining,
  onEditHistoricalTraining,
  onDeleteHistoricalTraining,
  editingHistoricalTrainingId,
  historicalTrainingActionsDisabled,
  onSoutHistoricalSaved,
  onProfriskHistoricalSaved,
  educationDocumentApi,
  onEducationDocumentChanged,
  consentApi,
  onConsentChanged,
  onTrainEmployee,
  trainEmployeeBusy,
}: {
  employee: EmployeeCard
  /** «Назад» — слева в верхней строке карточки (ответ заказчицы 04.11 к п.5.2). */
  backButton?: ReactNode
  /** Дерево разделов каталога — группировка «Обучений» как в каталоге. */
  directions?: TrainingDirectionItem[]
  /** Деактивация/активация (§10.3, design D1) — опционально: без обработчика кнопка не показывается. */
  onToggleActive?: () => void
  toggling?: boolean
  /**
   * «Изменить информацию о сотруднике» (§9.4, ADDED-спека company-structure) — опционально:
   * без обработчика кнопка не показывается. Доступно и клиенту, и команде (обе стороны имеют
   * updateEmployee через свой scope); оркестрацию формы правки держит employee-card-loader.
   */
  onEdit?: () => void
  /**
   * Открыть карточку рабочего места (p2-ot-section §9.5, design D5) — опционально: без
   * обработчика поле «Сменное рабочее место» рендерится как обычный текст, без ссылки.
   * Клиентская страница `/staff/:id` передаёт (навигация на `/workplaces/:id`); командная
   * вкладка «Сотрудники» — НЕ передаёт (D11, команда не получает эту ссылку).
   */
  onOpenWorkplace?: (workplaceId: string) => void
  /**
   * «Добавить историческое обучение» (§11.1, p2-manual-historical/IC-4) — опционально, по
   * образцу `onEdit`: без обработчика кнопка не показывается. Клик запускает загрузку каталога
   * программ в `employee-card-loader.tsx` (по образцу `startEdit`), затем показывает форму.
   */
  onAddHistoricalTraining?: () => void
  /** Каталог программ загружается (listPrograms в полёте) — кнопка показывает «Загрузка…». */
  addingHistoricalTrainingLoading?: boolean
  /** Показать форму вместо кнопки (программы уже загружены). */
  addingHistoricalTraining?: boolean
  /** Каталог программ (загружен `employee-card-loader.tsx` при клике на кнопку). */
  historicalTrainingPrograms?: Program[]
  /** Запись сохранена — закрыть форму и перезагрузить карточку (reloadToken в loader'е). */
  onHistoricalTrainingSaved?: () => void
  /** «Отмена» — закрыть форму без сохранения (без вызова API). */
  onCancelHistoricalTraining?: () => void
  /**
   * «Править» у исторической записи обучения — опционально, по образцу добавления: загрузчик
   * грузит каталог программ и передаёт `editingHistoricalTrainingId`, форма открывается на месте
   * строки. Сохранение и отмена — те же `onHistoricalTrainingSaved`/`onCancelHistoricalTraining`.
   */
  onEditHistoricalTraining?: (record: TrainingRecord) => void
  /** «Удалить» у исторической записи обучения; подтверждение и запрос — в загрузчике. */
  onDeleteHistoricalTraining?: (record: TrainingRecord) => void
  /** Запись, открытая на правку (каталог программ уже загружен). */
  editingHistoricalTrainingId?: string
  /** Идёт загрузка каталога или удаление — действия над записями временно недоступны. */
  historicalTrainingActionsDisabled?: boolean
  /**
   * Историческая СОУТ сохранена через `HistoricalSoutPanel` блока «Условия труда» (§1.1,
   * p3-structure-sout-training, code-review волны 2) — опционально: без обработчика панель
   * по-прежнему работает (свой список перезагружается сама), но сводка карточки не обновится
   * до следующего открытия. `employee-card-loader.tsx` передаёт `reloadToken`-бамп по образцу
   * `onHistoricalTrainingSaved`.
   */
  onSoutHistoricalSaved?: () => void
  /** Аналогично — исторические профриски сохранены через `HistoricalProfriskPanel` (§1.2). */
  onProfriskHistoricalSaved?: () => void
  /**
   * Управление документом об образовании (centrika-docs!59) — опционально: без scoped api и
   * колбэка блок «Документ об образовании» показывается только на просмотр/скачивание (карточка
   * у команды). Клиентская карточка передаёт свою поверхность api и колбэк обновления.
   */
  educationDocumentApi?: EducationDocumentApi
  onEducationDocumentChanged?: (doc: EducationDocumentStatus) => void
  /**
   * Управление согласием на обработку ПД — по образцу документа об образовании: без scoped api и
   * колбэка согласие показывается только на просмотр/скачивание (карточка у команды). Согласие
   * одно на человека и подтягивается во все его заказы обучения.
   */
  consentApi?: ConsentApi
  onConsentChanged?: (consent: TrainingConsentStatus) => void
  /**
   * «Обучить сотрудника» (training-order-entry-points; ТЗ п.5.1) — опционально: без обработчика
   * кнопки нет (команда заказы обучения не оформляет). Только у действующего сотрудника.
   */
  onTrainEmployee?: () => void
  /** Заказ обучения создаётся — кнопка недоступна. */
  trainEmployeeBusy?: boolean
}) {
  const indices = useMemo(() => employeeIndices(employee.trainings), [employee.trainings])
  return (
    <div className="flex flex-col gap-4">
      {/* Верхняя строка: «Назад» слева; справа «Активен», ФИО и действия (ответ заказчицы 04.11). */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>{backButton}</div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Badge variant={employee.isActive ? 'green' : 'gray'}>
            {employee.isActive ? 'Активен' : 'Уволен(а)'}
          </Badge>
          <h2 className="text-18 font-semibold text-txt">{employee.fullName}</h2>
          {employee.isMinor ? <Badge variant="amber">несовершеннолетний</Badge> : null}
          {/* Метка «инвалидность» СКРЫТА (centrika-docs, page.employee, MR !37) — признак не
              показывается в интерфейсе, но остаётся в данных сотрудника. */}
          {employee.isShift ? <Badge variant="blue">сменное рабочее место</Badge> : null}
          {onEdit ? (
            <Button size="sm" variant="secondary" onClick={onEdit}>
              Изменить информацию
            </Button>
          ) : null}
          {onToggleActive ? (
            <Button size="sm" variant="secondary" disabled={toggling} onClick={onToggleActive}>
              {toggling ? 'Сохранение…' : employee.isActive ? 'Уволен(а)' : 'Восстановить в штате'}
            </Button>
          ) : null}
        </div>
      </div>
      <div className="grid items-start gap-4 nw:grid-cols-2">
        <Card className="min-w-0" header="Информация">
          <InfoField label="Подразделение" value={employee.departmentName} />
          <InfoField label="Должность" value={employee.position} />
          <InfoField label="Пол" value={employee.gender === 'f' ? 'женский' : 'мужской'} />
          <InfoField label="Дата рождения" value={formatDate(employee.birthDate)} />
          <InfoField label="СНИЛС" value={employee.snils} />
          <InfoField label="Email" value={employee.email} />
          <InfoField label="Телефон" value={employee.phone} />
          <InfoField label="Образование" value={employee.education} />
          <InfoField
            label="Документ об образовании"
            value={
              educationDocumentApi && onEducationDocumentChanged ? (
                <EducationDocumentCell
                  api={educationDocumentApi}
                  employee={{
                    personId: employee.id,
                    educationDocument: employee.educationDocument,
                  }}
                  onChanged={(e) =>
                    onEducationDocumentChanged(e.educationDocument ?? { hasDocument: false })
                  }
                />
              ) : (
                <DocumentReadonly
                  loaded={Boolean(employee.educationDocument?.hasDocument)}
                  fileName={employee.educationDocument?.fileName}
                  url={employee.educationDocument?.url}
                  emptyLabel="Документ не загружен"
                  loadedLabel="Документ загружен"
                />
              )
            }
          />
          <InfoField
            label="Согласие на обработку ПД"
            value={
              consentApi && onConsentChanged ? (
                <ConsentCell
                  api={consentApi}
                  employee={{
                    personId: employee.id,
                    hasConsent: Boolean(employee.consent?.hasConsent),
                    consentFileName: employee.consent?.consentFileName,
                    consentUrl: employee.consent?.consentUrl,
                  }}
                  onChanged={onConsentChanged}
                />
              ) : (
                <DocumentReadonly
                  loaded={Boolean(employee.consent?.hasConsent)}
                  fileName={employee.consent?.consentFileName}
                  url={employee.consent?.consentUrl}
                  emptyLabel="Согласие не загружено"
                  loadedLabel="Согласие загружено"
                />
              )
            }
          />
          <InfoField
            label="Дата приёма на работу"
            value={employee.hiredOn ? formatDate(employee.hiredOn) : ''}
          />
          <InfoField label="Разряд" value={employee.rank} />
          {employee.isShift ? (
            <InfoField
              label="Сменное рабочее место"
              value={
                onOpenWorkplace ? (
                  <button
                    type="button"
                    className="text-accent underline"
                    onClick={() => onOpenWorkplace(employee.workplaceId)}
                  >
                    Открыть карточку места
                  </button>
                ) : (
                  'включено'
                )
              }
            />
          ) : null}
          {employee.isShift ? (
            <InfoField
              label="Сменщики"
              value={
                employee.shiftPartners.length > 0
                  ? employee.shiftPartners.map((p) => p.fullName).join(', ')
                  : ''
              }
            />
          ) : null}
          {employee.deactivatedAt ? (
            <InfoField label="Дата увольнения" value={formatDate(employee.deactivatedAt)} />
          ) : null}
        </Card>

        <div className="flex min-w-0 flex-col gap-4">
          <Card header="Условия труда — по последней СОУТ">
            {employee.workConditions ? (
              <>
                <Row label="Класс условий труда" value={employee.workConditions.hazardClass} />
                <Row
                  label="Дата проведения"
                  value={
                    employee.workConditions.conductedOn
                      ? formatDate(employee.workConditions.conductedOn)
                      : ''
                  }
                />
                <Row
                  label="Срок действия"
                  value={
                    employee.workConditions.perpetual
                      ? 'бессрочно'
                      : employee.workConditions.validUntil
                        ? formatDate(employee.workConditions.validUntil)
                        : ''
                  }
                />
              </>
            ) : (
              <EmptyState
                icon={ShieldCheck}
                title="Оценка СОУТ ещё не проводилась"
                className="py-6"
                description="Срез условий появится после первого результата СОУТ рабочего места"
              />
            )}
            <HistoricalSoutPanel
              workplaceId={employee.workplaceId}
              openLabel="Добавить историческую СОУТ"
              inlineRecords
              onSaved={onSoutHistoricalSaved}
            />
          </Card>
          <Card header="Информация по профрискам">
            {employee.profriskInfo ? (
              <>
                <Row
                  label="Уровень риска"
                  value={RISK_LEVEL_LABELS[employee.profriskInfo.riskLevel]}
                />
                <Row
                  label="Дата оценки"
                  value={
                    employee.profriskInfo.assessedOn
                      ? formatDate(employee.profriskInfo.assessedOn)
                      : ''
                  }
                />
              </>
            ) : (
              <EmptyState
                icon={AlertTriangle}
                title="Оценка профрисков ещё не проводилась"
                className="py-6"
                description="Сведения появятся после первого результата профрисков должности"
              />
            )}
            <HistoricalProfriskPanel
              workplaceId={employee.workplaceId}
              openLabel="Добавить исторические профриски"
              inlineRecords
              onSaved={onProfriskHistoricalSaved}
            />
          </Card>

          <Card
            header={
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span>Обучения</span>
                <div className="flex flex-wrap gap-2">
                  {onTrainEmployee && employee.isActive ? (
                    <Button size="sm" disabled={trainEmployeeBusy} onClick={onTrainEmployee}>
                      Обучить сотрудника
                    </Button>
                  ) : null}
                  {onAddHistoricalTraining && !addingHistoricalTraining ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={addingHistoricalTrainingLoading}
                      onClick={onAddHistoricalTraining}
                    >
                      {addingHistoricalTrainingLoading
                        ? 'Загрузка…'
                        : 'Добавить историческое обучение'}
                    </Button>
                  ) : null}
                </div>
              </div>
            }
          >
            {/* Индексы обучения цветом статуса (ответ заказчицы 04.11 к п.5.2). */}
            {indices.length > 0 ? (
              <div
                className="mb-3 flex flex-wrap items-center gap-1.5"
                aria-label="Индексы обучения"
              >
                {indices.map(({ index, status }) => (
                  <span
                    key={index}
                    className={cn(
                      'rounded-pill px-2 py-0.5 text-12 font-semibold',
                      INDEX_TONE[status],
                    )}
                    title={`${index} — ${TRAINING_STATUS_VIEW[status].label}`}
                  >
                    {index}
                  </span>
                ))}
              </div>
            ) : null}
            {addingHistoricalTraining ? (
              <HistoricalTrainingForm
                personId={employee.id}
                programs={historicalTrainingPrograms ?? []}
                onSaved={onHistoricalTrainingSaved ?? (() => {})}
                onCancel={onCancelHistoricalTraining ?? (() => {})}
              />
            ) : null}
            <TrainingBlock
              trainings={employee.trainings}
              directions={directions}
              personId={employee.id}
              editingId={editingHistoricalTrainingId}
              programs={historicalTrainingPrograms}
              actionsDisabled={historicalTrainingActionsDisabled}
              onEdit={onEditHistoricalTraining}
              onDelete={onDeleteHistoricalTraining}
              onSaved={onHistoricalTrainingSaved}
              onCancelEdit={onCancelHistoricalTraining}
            />
          </Card>
        </div>
      </div>
    </div>
  )
}
